import { alpha, Grid, Pagination, PaginationItem, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import 'react-grid-layout/css/styles.css';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import ButtonVitec from '../../../app/components/ButtonVitec';
import SVGIconBin from '../../../app/components/SVGIconBin';
import SVGIconEdit from '../../../app/components/SVGIconEdit';
import ToolTipTfWrapper from '../../../app/components/ToolTipTfWrapper';
import Utils from '../../../felles/Utils';
import { default as NN05, default as NN05Ansatt, default as NN05Employee } from '../../../models/NN05Employee';
import PageImpl from '../../../models/PageImpl';
import NN05FormListData from '../../../models/payload/NN05FormListData';
import * as actions from '../../../store/actions/index';
import * as actionsNN05 from '../../../store/actions/indexNN05';
import { AppState } from '../../../store/rootReducer';
import { useStyles } from '../styles/List.styles';
import NN05PERSONTableHead, { hInit } from './NN05TableHead';
import { TableHeaders } from '../../../models/Headers';

/**
 * Employee List Management Component
 * 
 * A comprehensive data table component for managing employee records in the NN05 system.
 * Provides advanced features including pagination, sorting, filtering, keyboard navigation,
 * search functionality, and CRUD operations for employee management.
 * 
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 05.08.2025 
 * 
 * Key Features:
 * - Paginated employee data display with Material-UI Table
 * - Real-time search and filtering capabilities
 * - Keyboard navigation with arrow keys and function key shortcuts
 * - Row selection and focus management
 * - Sortable columns with header controls
 * - Excel export functionality
 * - CRUD operations (Create, Read, Update, Delete)
 * - Responsive design with scroll position management
 * - Debounced scroll handling for performance
 * 
 * Search & Filtering:
 * - Name-based search with real-time updates
 * - Column-specific filtering through table headers
 * - Case-insensitive search functionality
 * - Reset functionality to clear all filters
 * 
 * Keyboard Shortcuts:
 * - F7: Previous page navigation
 * - F8: Next page navigation
 * - F9: Reset form and filters
 * - Arrow Up/Down: Navigate between table rows
 * - Enter: Edit selected employee or submit search
 * - Home: Navigate to first page
 * 
 * CRUD Operations:
 * - Create: "Ny ansatt" button opens employee creation form
 * - Read: Table displays paginated employee data
 * - Update: Click edit icon or press Enter on selected row
 * - Delete: Click delete icon to remove employee
 * 
 * Data Display:
 * - Employee Number, Name, Birth Date
 * - Phone Number, Employment Start/End Dates
 * - Action buttons for Edit and Delete operations
 * - Row highlighting for current selection
 * 
 * Performance Features:
 * - Debounced scroll event handling
 * - Efficient Redux state management
 * - Lazy loading with pagination
 * - Optimized re-rendering with React.memo patterns
 * 
 * @param props.size - Viewport size information for responsive behavior
 * @returns {JSX.Element} The employee list management component
 */

//{ search: string, page: number, size: number, sort: string, exportToExcel: boolean, tableHeaders: TableHeaders[] };

// Define props for the NN05PERSONFormHook component
interface NN05PERSONFormHookProps {
  size: { readonly width: number | null; readonly height: number | null; };
}

/**
 * Main functional component for employee list management
 * 
 * Manages the complete employee list interface including search, pagination,
 * table display, and navigation. Integrates with Redux for state management
 * and provides keyboard shortcuts for efficient user interaction.
 * 
 * Component Responsibilities:
 * - Initialize form state and default values
 * - Set up keyboard event listeners and scroll handlers
 * - Manage table row selection and focus states
 * - Handle pagination and sorting operations
 * - Coordinate CRUD operations with child forms
 * - Maintain scroll position across data updates
 * 
 * State Management:
 * - Form inputs (search, pagination, sorting)
 * - UI state (loading, focused row, scroll position)
 * - Employee data (page content, selected employee)
 * - Transaction states for form dialogs
 * 
 * @param props - Component props containing size information
 * @returns Employee list component with full CRUD functionality
 */
const NN05List: React.FC<NN05PERSONFormHookProps> = ({ size }) => {
  const { classes: cls } = useStyles();  // Accessing styles
  const dispatch = useDispatch();  // Redux dispatch
  const page: PageImpl<NN05> = useSelector((state) => (state as AppState).NN05.page);
  const [defaultPageCount, setDefaultPageCount] = useState(10);
  const tableHeaders: TableHeaders[] = useSelector((state) => (state as AppState).NN05.tableHeaders);
  const [currentFocusedField, setCurrentFocusedField] = useState('');
  const isLoading: boolean = useSelector((state) => (state as AppState).utils.isLoading);
  const currentRowFocused: number = useSelector((state) => (state as AppState).NN05.currentRowFocused);
  const scrollTopPosition: number = useSelector((state) => (state as AppState).NN05.scrollTopPosition);
  const [scrollPosition, setScrollPosition] = useState(0); // State to store scroll position
  const tableContainerRef = useRef<HTMLDivElement>(null); // Ref for the scrollable container

  // Redux state for key pressed. -1 enable keys on this component, 0 disable keys on this component and enable keys on subcomponent.
  const stateKeypressed: number = useSelector((state) => (state as AppState).NN05.stateKeypressed);
  const transStateInsertEmployee: number = useSelector((state) => (state as AppState).NN05.transStateInsertEmployee);
  const transStateUpdateEmployee: number = useSelector((state) => (state as AppState).NN05.transStateUpdateEmployee);
  const transStateDialogEmployee: number = useSelector((state) => (state as AppState).NN05.transStateDialogEmployee);

  const methods = useForm<NN05FormListData>({
    defaultValues: { companyId: Utils.T_companyNr, search: '', pageSize: 100, sort: 'employeeId', exportToExcel: false, tableHeaders: [], page: 0 },
  });

  /**
   * Formats date components into a readable date string (dd.mm.yyyy)
   * 
   * @param day - Day component (can be string or number)
   * @param month - Month component (can be string or number)
   * @param year - Year component (can be 2-digit or 4-digit)
   * @returns Formatted date string in dd.mm.yyyy format
   */
  const formatDateDisplay = (day: string | number, month: string | number, year: string | number): string => {
    if (!day || !month || !year) return '';
    const yearStr = String(year).replace('.0', '');
    return `${day}.${month}.${Utils.getfullYearDataFromTwoDigitedYear(yearStr)}`;
  };

  /**
   * Filters and formats termination date display for employee records
   * 
   * Determines whether to display a termination date based on data validity and relevance.
   * Implements business logic to avoid displaying redundant or empty termination dates.
   * 
   * Business Logic:
   * - Returns empty string if all date components are spaces (employee still active/no termination)
   * - Returns empty string if termination date equals hire date (redundant/meaningless data)
   * - Formats date in dd.mm.yyyy format if termination date exists and differs from hire date
   * - Uses two-digit year conversion for display consistency
   * 
   * Use Cases:
   * - Active employees with no termination: Shows blank (all date fields are spaces)
   * - Employees terminated on hire date: Shows blank (data entry error or same-day termination)
   * - Normally terminated employees: Shows formatted termination date
   * 
   * @param {NN05} n - Employee object containing hire and termination date components
   * @returns {string | undefined} Formatted termination date string or empty string
   */
  const FilterTerminationDateDisplay = (n: NN05): string | undefined => {
    if (n.terminationDateDay == '  ' && n.terminationDateMonth == '  ' && n.terminationDateYear == '  ')
      return '';


    if (n.terminationDateDay === n.hireDateDay && n.terminationDateMonth === n.hireDateMonth && n.terminationDateYear === n.hireDateYear)
      return '';


    return `${n.terminationDateDay}.${n.terminationDateMonth}.${Utils.getfullYearDataFromTwoDigitedYear(n.terminationDateYear)}`;
  };

  /**
   * Primary initialization effect for component setup and event listeners
   * 
   * Handles initial data loading, scroll event management, and global keyboard shortcuts.
   * Sets up performance-optimized event listeners with proper cleanup.
   * 
   * Initialization Tasks:
   * - Triggers initial data load if not currently loading
   * - Sets up scroll position tracking for table container
   * - Registers global keyboard event listeners for pagination shortcuts
   * - Restores previous scroll position from Redux state
   * 
   * Scroll Management:
   * - Debounced scroll event handling for performance
   * - Maintains scroll position across page navigation
   * - Updates local state with current scroll position
   * 
   * Keyboard Event Setup:
   * - Window-level event listener for F7/F8 pagination shortcuts
   * - Prevents default browser behavior for handled keys
   * - Enables keyboard navigation without focus requirements
   * 
   * Cleanup Tasks:
   * - Removes all event listeners to prevent memory leaks
   * - Cleans up scroll event handlers
   * - Ensures proper component unmounting
   * 
   * Dependencies: [] - Runs only on component mount/unmount
   */
  {/* --------------------------------------- Effects ------------------------------------- */ }
  useEffect(() => {
    if (!isLoading)
      onSubmit(methods.getValues()); // Reset the form state when the component mounts
    //
    const handleScroll = (e: any) => {
      if (tableContainerRef.current) {
        setScrollPosition(tableContainerRef.current.scrollTop); // Update scroll position
      }
    };
    // NettbrettNETTBRETT hack - Prevent default touchmove behavior
    window.addEventListener('keydown', handleKeyUpPressedAtWindowDocumentLevel);

    // Get the table container
    const tableContainer = tableContainerRef.current;

    // Add event listener for scroll
    if (tableContainer) {
      tableContainer.addEventListener('scroll', Utils.debounce_new((e: Event) => { handleScroll(e) }));
      tableContainer.scrollTop = scrollTopPosition;
    }
    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyUpPressedAtWindowDocumentLevel);
      //
      if (tableContainer) {
        tableContainer.removeEventListener(
          'scroll',
          Utils.debounce_new((e: Event) => { handleScroll(e); })
        );
      }
    };



  }, [stateKeypressed]);

  /**
   * Component state synchronization and UI management effect
   * 
   * Handles data-dependent initialization, focus management, and keyboard event setup.
   * Responds to changes in page content, loading state, and user interactions.
   * 
   * UI State Management:
   * - Updates pagination count based on server response
   * - Resets sandwich button state for consistent UI
   * - Manages input field focus for optimal user experience
   * 
   * Data Initialization:
   * - Loads column headers if not already present
   * - Initializes table structure with default configuration
   * - Sets up sortable column definitions
   * 
   * Focus Management:
   * - Auto-focuses search field when no data is present
   * - Manages focus state transitions
   * - Provides accessible navigation patterns
   * 
   * Keyboard Event Setup:
   * - Document-level event listeners for table navigation
   * - Arrow key handling for row selection
   * - Enter key handling for row actions
   * 
   * Touch Device Support:
   * - Removes touch event listeners to prevent conflicts
   * - Optimizes for tablet/mobile interaction patterns
   * 
   * Dependencies: [page.content, tableHeaders, isLoading, currentRowFocused, currentFocusedField, dispatch]
   * - Re-runs when data, headers, or focus state changes
   */
  useEffect(() => {
    // NettbrettNETTBRETT hack - Remove event listeners on component unmount
    document.body.removeEventListener('touchmove', Utils.preventDefault);

    // Set default page count based on content
    page.content?.length > 0 ? setDefaultPageCount(page.totalPages) : setDefaultPageCount(10);

    // Reset sandwich button state on load
    dispatch(actions.setSandwichButtonStateClicked(false));

    // Automatically focus on the first input field (alfa) when the component mounts
    if (currentFocusedField === '' && page.content?.length == 0) {
      const inputField: HTMLInputElement | null = document.querySelector('input[name="search"]');
      if (inputField && inputField.value === '') {
        inputField.focus();
      }
    }
    // Load column headers if not already loaded
    if (tableHeaders.length === 0) {
      dispatch(actionsNN05.NN05UpdateColumnHeaderList(hInit));
    }
    // Add event listeners for keyboard shortcuts
    document.addEventListener('keydown', handleKeyUpPressedAtDocumentLevel);

    return () => {
      // Remove event listeners for keyboard shortcuts
      document.removeEventListener('keydown', handleKeyUpPressedAtDocumentLevel);
    };
  }, [page.content, tableHeaders, isLoading, currentRowFocused, currentFocusedField, dispatch]);

  /**
   * Handles window-level keyboard shortcuts for pagination and navigation
   * 
   * Provides global keyboard shortcuts that work regardless of focus state,
   * enabling efficient pagination and form reset operations.
   * 
   * Supported Shortcuts:
   * - F7: Navigate to previous page (if available)
   * - F8: Navigate to next page (if available)
   * - F9: Reset form and clear all filters
   * - Home: Navigate to first page
   * 
   * Pagination Logic:
   * - Validates page boundaries before navigation
   * - Updates form state and triggers data reload
   * - Maintains current search and filter criteria
   * 
   * Reset Functionality:
   * - Clears all form inputs and filters
   * - Resets component state to initial values
   * - Focuses on search field for immediate use
   * 
   * Event Handling:
   * - Prevents default browser behavior for handled keys
   * - Uses switch statement for efficient key routing
   * - Integrates with React Hook Form for state management
   * 
   * @param {KeyboardEvent} event - The keyboard event from window listener
   */
  const handleKeyUpPressedAtWindowDocumentLevel = (event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    const actionSource = key || (event.target && (event.target as HTMLInputElement).value);
    //
    if (key === 'Delete' && (transStateInsertEmployee > 0 || transStateUpdateEmployee > 0 || transStateDialogEmployee > 0)) {
      event.preventDefault();
      return;
    }
    //
    if (key === 'Home' || key === 'F7' || key === 'F8') {
      event.preventDefault();
      const index = page.content.findIndex(item => +item.employeeId === currentRowFocused);
      switch (true) {
        // F7 navigates to the previous page
        case actionSource === 'F7':
          if (methods.getValues('page') >= 1) {
            methods.setValue('page', methods.getValues('page') - 1)
            methods.handleSubmit(onSubmit)();
          }
          break;
        // F8 navigates to the next page
        case actionSource === 'F8':
          if (methods.getValues('page') < defaultPageCount - 1) {
            methods.setValue('page', methods.getValues('page') + 1)
            methods.handleSubmit(onSubmit)();
          }
          break;
        // Reset button resets the form and data
        case actionSource === 'F9': {
          // Reset the form state
          methods.reset();
          // Reset the child component state
          dispatch(actionsNN05.NN05ResetForm(true));
          // Focus on the first input field (alfa) after reset
          const inputField: HTMLInputElement | null = document.querySelector('input[name="alfaKode"]');
          if (inputField && inputField.value === '') {
            inputField.focus();
          }
          break;
        }
      }
    }
  }

  /**
   * Handles document-level keyboard events for table navigation and row actions
   * 
   * Provides keyboard navigation within the employee table, including row selection
   * and action triggers. Enables efficient data browsing without mouse interaction.
   * 
   * Navigation Controls:
   * - Arrow Up: Move to previous employee row
   * - Arrow Down: Move to next employee row
   * - Enter: Edit selected employee (if no dialog is open)
   * 
   * Row Selection Logic:
   * - Maintains current row focus across navigation
   * - Updates Redux state with selected employee
   * - Handles boundary conditions (first/last row)
   * - Provides visual feedback for current selection
   * 
   * Edit Action Integration:
   * - Triggers edit form for currently focused employee
   * - Checks dialog state to prevent conflicts
   * - Integrates with transaction state management
   * 
   * State Management:
   * - Updates currentRowFocused in Redux store
   * - Sets selected employee for edit operations
   * - Maintains selection across data updates
   * 
   * Boundary Handling:
   * - Prevents navigation beyond available data
   * - Handles empty data sets gracefully
   * - Maintains selection on first/last items
   * 
   * @param {KeyboardEvent} event - The keyboard event from document listener
   */
  const handleKeyUpPressedAtDocumentLevel = (event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    const actionSource = key || (event.target && (event.target as HTMLInputElement).value);
    const index = page.content.findIndex(item => +item.employeeId === currentRowFocused);

    //Utils.log('handleKeyUpPressedAtDocumentLevel', actionSource, 'currentRowFocused:', currentRowFocused, 'index:', index, 'page.content.length:', page.content.length);
    //Utils.log('currentRowFocused', currentRowFocused, 'stateKeypressed', stateKeypressed);

    /**
     * Scrolls the table container to make the selected row visible
     * 
     * Calculates the optimal scroll position to center the selected row
     * in the visible area of the table container.
     * 
     * @param {number} rowIndex - The index of the row to scroll to
     */
    const scrollToRow = (rowIndex: number, $rowHeight: number, $headerHeight: number) => {
      if (tableContainerRef.current && rowIndex >= 0) {
        // Calculate row height (assuming consistent row height)
        const rowHeight = $rowHeight; // Approximate height of each table row
        const headerHeight = $headerHeight; // Height of table header
        const containerHeight = tableContainerRef.current.clientHeight;

        // Calculate the position of the target row
        const rowTop = headerHeight + (rowIndex * rowHeight);
        const rowBottom = rowTop + rowHeight;

        // Get current scroll position
        const currentScrollTop = tableContainerRef.current.scrollTop;
        const visibleTop = currentScrollTop;
        const visibleBottom = currentScrollTop + containerHeight;

        // Check if row is already visible
        if (rowTop < visibleTop) {
          // Row is above visible area, scroll up
          tableContainerRef.current.scrollTo({
            top: rowTop - headerHeight - 10, // Add small offset
            behavior: 'smooth'
          });
        } else if (rowBottom > visibleBottom) {
          // Row is below visible area, scroll down
          tableContainerRef.current.scrollTo({
            top: rowBottom - containerHeight + 10, // Add small offset
            behavior: 'smooth'
          });
        }
        // If row is already visible, no scrolling needed
      }
    };
    //setKey
    switch (true) {
      case actionSource === 'ArrowUp': {
        if (currentRowFocused === +page.content[0]?.employeeId) {
          return;
        }
        const activeUp = (page.content[index - 1]?.employeeId ? page.content[index - 1].employeeId : page.content[0]?.employeeId ? page.content[page.content.length - 1]?.employeeId : 0);
        dispatch(actionsNN05.setCurrentRowFocused(activeUp));
        // Find the employee object that corresponds to activeUp
        const selectedEmployee = page.content.find(emp => emp.employeeId === activeUp);
        if (selectedEmployee) {
          dispatch(actionsNN05.setSelelectedEmployee(selectedEmployee));
        }

        // Scroll to the selected row
        const newIndex = page.content.findIndex(item => item.employeeId === activeUp);
        scrollToRow(newIndex, 40, 0);
        break;
      }
      case actionSource === 'ArrowDown': {
        // If already at last row, do nothing
        if (currentRowFocused === +page.content[page.content.length - 1]?.employeeId) {
          return;
        }
        let activeDown;
        if (index === -1) {
          // No current selection, go to first item
          activeDown = page.content[0]?.employeeId || 0;
        } else if (index === page.content.length - 1) {
          // At last item, stay there
          activeDown = currentRowFocused;
        } else {
          // Move to next item
          activeDown = page.content[index + 1]?.employeeId || 0;
        }
        dispatch(actionsNN05.setCurrentRowFocused(activeDown));
        // Find the employee object that corresponds to activeDown
        const selectedEmployee = page.content.find(emp => emp.employeeId === activeDown);
        if (selectedEmployee) {
          dispatch(actionsNN05.setSelelectedEmployee(selectedEmployee));
        }
        // Scroll to the selected row
        const newIndex = page.content.findIndex(item => item.employeeId === activeDown);
        scrollToRow(newIndex, 43, 50);
        break;
      }
      case (actionSource === 'Enter'
        && stateKeypressed === -1
        && transStateInsertEmployee > 0
        && transStateUpdateEmployee > 0
        && transStateDialogEmployee > 0
      ): {
          event.preventDefault();
          if (currentRowFocused !== -1) {
            handleUserEdit();
            dispatch(actionsNN05.setLockKeypressed(0));
          }
          break;
        } case (actionSource === 'Delete'
          && stateKeypressed === -1
          && transStateInsertEmployee === 0
          && transStateUpdateEmployee === 0
          && transStateDialogEmployee === 0
        ): {
          event.preventDefault();
          if (currentRowFocused !== -1) {
            dispatch(actionsNN05.setTransStateDeleteEmployee(1));
            dispatch(actionsNN05.setRawData(page.content[index - 1]));
            dispatch(actionsNN05.setListFormData(methods.getValues()));
            dispatch(actionsNN05.setCurrentRowFocused(page.content[index - 1]?.employeeId));
            dispatch(actionsNN05.setSelelectedEmployee(page.content[index]));
            dispatch(actionsNN05.setLockKeypressed(0));
          }
          break;
        }
    }
    //
    if (stateKeypressed >= 0) {
      dispatch(actionsNN05.setLockKeypressed(-1));
    }
  };

  /**
   * Handles mouse click selection of table rows
   * 
   * Manages user interaction when clicking on employee table rows,
   * updating selection state and preserving scroll position.
   * 
   * Selection Actions:
   * - Sets the clicked employee as selected in Redux store
   * - Updates current row focus to the selected employee
   * - Preserves current scroll position for navigation consistency
   * 
   * State Updates:
   * - selectedEmployee: Complete employee object for editing
   * - currentRowFocused: Employee number for UI highlighting
   * - scrollTopPosition: Current scroll position for restoration
   * 
   * User Experience:
   * - Provides immediate visual feedback for selection
   * - Maintains scroll position when navigating between forms
   * - Enables subsequent keyboard navigation from clicked position
   * 
   * Integration:
   * - Works with keyboard navigation for hybrid interaction
   * - Supports both mouse and keyboard workflows
   * - Maintains consistent state across interaction methods
   * 
   * @param {MouseEvent} event - The mouse click event
   * @param {NN05Ansatt} n - The employee object that was clicked
   */
  const handleRowSelection = (event: React.MouseEvent<HTMLTableRowElement>, n: NN05Ansatt) => {
    dispatch(actionsNN05.setSelelectedEmployee(n));
    dispatch(actionsNN05.setScrollTopPosition(scrollPosition));
    dispatch(actionsNN05.setCurrentRowFocused(n.employeeId));
  };

  /**
   * Handles form-level keyboard shortcuts and button actions
   * 
   * Processes keyboard events from form elements and button clicks,
   * managing search submission, data export, and form reset operations.
   * 
   * Supported Actions:
   * - Enter: Submits search form with current criteria
   * - Export: Triggers Excel export of current data
   * - Reset: Clears all form inputs and resets state
   * 
   * Search Submission:
   * - Updates column headers with current configuration
   * - Resets page number to 0 for new search
   * - Maintains sort order and other settings
   * - Triggers immediate data reload
   * 
   * Export Functionality:
   * - Sets exportToExcel flag for server processing
   * - Includes current search and filter criteria
   * - Resets page to capture all matching records
   * 
   * Reset Operations:
   * - Clears form inputs to default values
   * - Resets component state across all child components
   * - Clears row selection and focus
   * - Returns focus to search field for immediate use
   * 
   * Event Handling:
   * - Prevents default browser behavior
   * - Uses action source identification for button clicks
   * - Integrates with React Hook Form validation
   * 
   * @param {React.KeyboardEvent<HTMLFormElement>} event - The triggering event
   */
  const handleKeyPress = (event: React.KeyboardEvent<HTMLFormElement>) => {

    // Get the key pressed
    const { key } = event;
    const actionSource = key || (event.target && (event.target as HTMLInputElement).value);
    event.preventDefault();
    //setKey
    switch (true) {
      // Enter submits the form
      case (actionSource === 'Enter'):
        methods.setValue('tableHeaders', hInit);
        dispatch(actionsNN05.NN05UpdateColumnHeaderList(hInit));
        methods.setValue('exportToExcel', false);
        methods.setValue('page', 0)
        methods.handleSubmit(onSubmit)();
        break;

      case (actionSource === 'Export'):
        actionSource === 'Export' ? methods.setValue('exportToExcel', true) : methods.setValue('exportToExcel', false);
        methods.setValue('page', 0)
        methods.handleSubmit(onSubmit)();
        break;
      // Reset button resets the form and data, then triggers search
      case actionSource === 'Reset': {
        // Reset the form state
        methods.reset();
        dispatch(actionsNN05.setCurrentRowFocused(-1));
        // Reset the child component state
        dispatch(actionsNN05.NN05ResetForm(true));
        dispatch(actionsNN05.setKeyValuePairs4NN05([])); // Reset the key value pairs
        // Reset headers and trigger search
        methods.setValue('tableHeaders', hInit);
        dispatch(actionsNN05.NN05UpdateColumnHeaderList(hInit));
        methods.setValue('exportToExcel', false);
        methods.setValue('page', 0);
        methods.handleSubmit(onSubmit)();
        break;
      }
      default:
        return;
    }
  };

  /**
   * Handles form submission and data loading
   * 
   * Central function for processing search requests, pagination changes,
   * and data export operations. Manages API calls and loading states.
   * 
   * Data Processing:
   * - Validates and normalizes form input data
   * - Sets default column headers if not provided
   * - Prevents duplicate submissions during loading
   * - Resets export flag after processing
   * 
   * API Integration:
   * - Dispatches API request with complete search criteria
   * - Includes pagination, sorting, and filtering parameters
   * - Handles both regular data loading and Excel export
   * 
   * Loading State Management:
   * - Checks current loading state to prevent duplicates
   * - Ensures single active request at a time
   * - Maintains UI responsiveness during data fetch
   * 
   * Export Functionality:
   * - Processes Excel export requests with current filters
   * - Resets export flag after dispatch
   * - Maintains search criteria for export consistency
   * 
   * State Consistency:
   * - Preserves form state across submissions
   * - Maintains sort order and column configuration
   * - Ensures data integrity throughout the process
   * 
   * @param {FormInputs} data - Form data containing search and pagination parameters
   */
  // Function to handle form submission
  const onSubmit = (data: any) => {

    methods.setValue('companyId', Utils.T_companyNr);
    if (data.tableHeaders.length === 0) {
      data.tableHeaders = hInit;
    }
    // Check if the form is in loading state and prevent multiple submissions
    if (!isLoading) {
      dispatch(actionsNN05.NN05PERSONListeGet(data));
      methods.setValue('exportToExcel', false);
      //Reset scroll position after data is dispatched
      //dispatch(actionsNN05.setScrollTopPosition(0));
    }
    // }
  };

  /**
   * Handles pagination changes from Material-UI Pagination component
   * 
   * Processes user clicks on pagination controls, updating the current page
   * and triggering data reload with new page parameters.
   * 
   * Page Calculation:
   * - Offsets pageValue by 1 (Material-UI uses 1-based indexing)
   * - Updates form state with new page number
   * - Preserves all other form parameters (search, sort, filters)
   * 
   * Data Reload:
   * - Triggers form submission with updated page number
   * - Maintains current search and filter criteria
   * - Preserves sort order and page size settings
   * 
   * User Experience:
   * - Provides immediate feedback through loading states
   * - Maintains scroll position context across page changes
   * - Updates pagination controls to reflect new state
   * 
   * @param {Event} e - The pagination click event
   * @param {number} pageValue - The new page number (1-based from Material-UI)
   */
  const pagechange = (e: any, pageValue = 0) => {
    methods.setValue('page', pageValue - 1)// offsetting by 1
    methods.handleSubmit(onSubmit)();
  };

  /**
   * Handles column sorting changes from table header interactions
   * 
   * Updates the sort configuration when users interact with sortable column headers,
   * triggering immediate data reload with new sort parameters.
   * 
   * Sort Configuration:
   * - Receives updated header configuration with sort direction
   * - Updates form state with new column headers
   * - Maintains current search and filter criteria
   * 
   * Data Reload:
   * - Triggers immediate form submission with new sort order
   * - Preserves pagination position when possible
   * - Updates table display with newly sorted data
   * 
   * Header State Management:
   * - Updates Redux store with new header configuration
   * - Maintains column visibility and filter settings
   * - Preserves column width and display preferences
   * 
   * @param {TableHeaders[]} value - Updated column header configuration with sort settings
   */
  const setSort = (value: TableHeaders[]) => {
    methods.setValue('tableHeaders', value);
    methods.handleSubmit(onSubmit)();
  };

  /**
   * Checks if any column filters are currently active
   * 
   * Utility function to determine if the table has active filters,
   * used for conditional rendering and UI state management.
   * 
   * Filter Detection:
   * - Examines all column headers for non-empty filter values
   * - Returns true if any column has an active filter
   * - Used to determine when to show data even with empty results
   * 
   * UI Integration:
   * - Controls table visibility when no data is present
   * - Influences "No data" message display logic
   * - Supports filter-aware UI behavior
   * 
   * @returns {boolean} True if any column has an active filter
   */
  const hasHearderListAFilterActive = (): boolean => {
    return tableHeaders.some((header) => header.filter !== '');
  };

  /**
   * Initiates employee creation process
   * 
   * Opens the employee creation form by setting appropriate transaction state
   * and preserving current list context for form integration.
   * 
   * State Management:
   * - Sets list form data for context preservation
   * - Updates transaction state to trigger form display
   * - Maintains current search and pagination context
   * 
   * Form Integration:
   * - Provides current list state to creation form
   * - Enables return to same list position after creation
   * - Supports consistent user workflow
   * 
   * @param {FormInputs} listFormData - Current form state for context preservation
   */
  interface HandleSingleInsertProps {
    companyId?: string;
    search?: string;
    pageSize?: number;
    sort?: string;
    exportToExcel?: boolean;
    tableHeaders?: TableHeaders[];
    page?: number;
    [key: string]: any;
  }

  const handleSingleInsert = (listFormData: HandleSingleInsertProps) => {
    dispatch(actionsNN05.setListFormData(listFormData));
    dispatch(actionsNN05.setTransStateInsertEmployee(1));
  };

  /**
   * Initiates employee editing process for the currently selected employee
   * 
   * Opens the employee edit form with the currently focused employee data,
   * preserving list context for seamless navigation back to the list.
   * 
   * State Management:
   * - Captures current form state for context preservation
   * - Sets transaction state to trigger edit form display
   * - Uses currently focused employee for editing
   * 
   * Edit Context:
   * - Provides current list state to edit form
   * - Maintains search and pagination context
   * - Enables return to exact same list position
   * 
   * Integration:
   * - Called by keyboard Enter key on selected row
   * - Called by edit icon click handlers
   * - Supports both keyboard and mouse workflows
   */
  const handleUserEdit = () => {
    dispatch(actionsNN05.setListFormData(methods.getValues()));
    dispatch(actionsNN05.setTransStateUpdateEmployee(1));
  };

  // Handles single employee deletion
  const handleSingleDelete = (event: React.MouseEvent<HTMLDivElement>, rowData: NN05Employee, listFormData: HandleSingleInsertProps) => {
    dispatch(actionsNN05.setTransStateDeleteEmployee(1));
    dispatch(actionsNN05.setRawData(rowData));
    dispatch(actionsNN05.setListFormData(listFormData));
    dispatch(actionsNN05.setSelelectedEmployee(rowData));
    // Set the current row focused to the employee number
    if ((event.target as HTMLElement).id === 'DELETE_ICON')
      dispatch(actionsNN05.setCurrentRowFocused(rowData.employeeId));
  };

  // Rendering the form and table
  return (
    <FormProvider {...methods}>
      <form className={cls.root} onKeyUp={handleKeyPress}>
        <input  {...methods.register("companyId")} hidden />
        <input  {...methods.register("page")} hidden />
        <input  {...methods.register("pageSize")} hidden />
        <input  {...methods.register("sort")} hidden />
        <input  {...methods.register("exportToExcel")} hidden />
        <Paper elevation={0} sx={{ backgroundColor: alpha('#fff', .6) }} >
          <TableContainer sx={{ borderRadius: '5px'}} >
            <Grid container width={'auto'} height={page.content.length > 0 ? `calc(100vh - 122px)` : 'auto'} style={{ maxHeight: '100vh' }}>
              {/* HEADER SECTION */}
              <Grid container height={'80px'} className={cls.ba} sx={{ width: '100%' }} >
                <Grid container height={'10px'} alignItems="center" justifyContent='left' className={cls.bb} />
                <Grid container height={'40px'} className={cls.bb} direction="row"
                  justifyContent="space-between" alignItems="flex-end" sx={{ width: '100%', paddingLeft: 2, paddingRight: 2 }}>
                  <Grid container direction="row" alignItems="flex-end" sx={{ width: 'auto', flex: '0 0 auto', gap: 2 }}>
                    <Grid sx={{ minWidth: '300px' }} >
                      <ToolTipTfWrapper id='talfa'
                        title={<div>Søkefelt for navn: Første bokstav i et navn kan gi et treff.</div>}
                        placement={"top"} delay={8000}>
                        <TextField label="Navn" autoComplete="off" className={cls.tField} inputProps={{ shrink: 'false' }}
                          {...methods.register("search", { required: false })}
                          onClick={(e) => { dispatch(actionsNN05.setCurrentRowFocused(-1)); }}
                          onChange={(e) => {
                            dispatch(actionsNN05.setCurrentRowFocused(-1));
                            methods.setValue('search', (e.target as any).value);
                          }}
                        />
                      </ToolTipTfWrapper>
                    </Grid>
                    <Grid>
                      <ButtonVitec _onclick={(e) => handleKeyPress({ key: 'Enter', target: e.target, preventDefault: () => e.preventDefault() } as React.KeyboardEvent<HTMLFormElement>)} _width={105} _height={37} _weight={600} _type={'button'}
                        _label={'Søk'} _value={'Enter'} _tooltip={'Klikk på knappen for å sende inn skjemaet, eller trykk Enter'} />
                    </Grid>
                    <Grid>

                      <ButtonVitec _onclick={() =>
                        handleKeyPress({
                          key: 'Reset',
                          target: { value: 'Reset' },
                          preventDefault: () => { },
                        } as any)
                      } _width={105} _height={37} _weight={600} _type={'button'}
                        _label={'NULLSTILL'} _value={'Reset'} _tooltip={'Tilbakestill skjemaet F9'}
                        disabled={
                          false
                          //page.content.length === 0 && !hasHearderListAFilterActive()
                        }
                        _color={'red'} />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" alignItems="flex-end" sx={{ width: 'auto', flex: '0 0 auto', gap: 1 }}>
                    <Grid>
                      <ButtonVitec
                        _onclick={() => { handleSingleInsert(methods.getValues()) }}
                        _width={120}
                        _height={37}
                        _weight={600}
                        _type={'button'}
                        _label={'Ny ansatt'}
                        _tooltip={'Opprett ny ansatt'}
                      />
                    </Grid>
                    <Grid>
                      <ButtonVitec _onclick={(e) => handleKeyPress({ key: 'Export', target: e.target, preventDefault: () => e.preventDefault() } as React.KeyboardEvent<HTMLFormElement>)} _width={105} _height={37} _type={'button'} _color={'green'}
                        _label={'EXCEL'} _value={'Export'} disabled={page.content.length === 0} />
                    </Grid>
                  </Grid>
                </Grid>
                {/* CHECKBOX SECTION */}
                <Grid container height={'5px'} alignItems="center" justifyContent='left' className={cls.bb} />
              </Grid>
              {/* CONTENT SECTION - Table */}
              <Grid container
                width={'100%'}
                height={page.content.length > 0 ? `calc(100vh - 225px)` : 'auto'}
                alignItems="top"
                justifyContent='center'
                className={cls.ba}
                ref={tableContainerRef}
                sx={{
                  maxHeight: '100vh', overflow: 'auto',
                  "&::-webkit-scrollbar-thumb": { backgroundColor: "#706c6a", },
                }}
              >
                {page.content.length > 0 || hasHearderListAFilterActive() ?
                  <Grid width={'100%'} >
                    <Table stickyHeader className={cls.table} size="small" aria-labelledby="tableTitle" >
                      <NN05PERSONTableHead onRequestSort={setSort} />
                      <TableBody sx={{ width: '100%' }}>
                        {
                          Array.isArray(page.content) && page.content?.length > 0 ? (
                            page.content.map((n: NN05) => (
                              <TableRow
                                aria-checked={false}
                                key={n.employeeId}
                                selected={false}
                                sx={{
                                  backgroundColor:
                                    currentRowFocused == +n.employeeId ? '#d8eff2' : '#fff',
                                  border: '1px solid #fff',
                                  '&:hover': {
                                    backgroundColor: currentRowFocused == + n.employeeId ? '#d8eff2' : '#f3f3f3', // Change this to your desired hover color
                                  }
                                }}
                                onClick={(event) => { handleRowSelection(event, n) }}
                              >
                                <TableCell className={cls.tC} sx={{ width: '10%' }}>{n.employeeId}</TableCell>
                                <TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Navn')?.width }}>{n.name}</TableCell>
                                {/*<TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Fødselsdato')?.width }}>{n.PersonNumberBirthDay + '.' + n.PersonNumberBirthMonth + '.' + Utils.getfullYearDataFromTwoDigitedYear(n.PersonNumberBirthYear.replace('.0', ''))}</TableCell>*/}
                                <TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Fødselsdato')?.width }}>{formatDateDisplay(n.personNumberBirthDay, n.personNumberBirthMonth, n.personNumberBirthYear)}</TableCell>
                                <TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Telefon')?.width }}>{n.phoneNumber}</TableCell>
                                <TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Fra')?.width }}>
                                  {formatDateDisplay(n.hireDateDay, n.hireDateMonth, n.hireDateYear)}</TableCell>
                                <TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Til')?.width }}>
                                  {FilterTerminationDateDisplay(n)}
                                </TableCell>
                                <TableCell className={cls.tC} sx={{ width: hInit.find(hd => hd.id === 'Telefon')?.width }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div onClick={handleUserEdit}
                                      style={{ cursor: 'pointer', marginTop: 3 }}>
                                      <SVGIconEdit title="Edit" width={30} />
                                    </div>
                                    <div onClick={event => handleSingleDelete(event, n, methods.getValues())}
                                      style={{ cursor: 'pointer', marginTop: 2 }}>
                                      <SVGIconBin width={21} />
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} className={cls.tC} scope="row" padding="none">
                                No data available
                              </TableCell>
                            </TableRow>
                          )
                        }
                      </TableBody>
                    </Table>
                  </Grid>
                  : null}
              </Grid>
              {/*********************************************************** FOOTER *****************************************************************/}
            </Grid>
          </TableContainer>
          {/* FOOTER SECTION - Pagination */}
          {page.content.length > 0 ?
            <Grid container height={"39px"} alignItems="center" justifyContent='center'
              className={cls.bb} rowSpacing={0} columnSpacing={0}>
              <Grid>
                <Pagination sx={{
                  color: 'white',
                  "& .Mui-selected": {
                    backgroundColor: "#fff"
                  }
                }}
                  count={defaultPageCount}
                  onChange={(e, value) => pagechange(e, value)}
                  onKeyUp={(e) => pagechange(e)}
                  page={Math.max(1, (methods.getValues('page') || 0) + 1)}
                  renderItem={(item) => (
                    <PaginationItem sx={{ fontSize: '1.2em', }}
                      {...item}
                    />
                  )}
                />
              </Grid>
            </Grid>
            : null}</Paper>
      </form>
    </FormProvider >
  );
};
// Exporting the NN05Form component
export default NN05List;